import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  noIndex?: boolean;
  schemaJson?: object | object[];
  type?: string;
  article?: {
    publishedTime?: string;
    author?: string;
    section?: string;
  };
}

const BASE_URL = "https://agence8020.com";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = "Agence 8020";

/**
 * Centralized SEO component that ensures complete OG/Twitter metadata
 * on every page. Provides defaults while allowing page-specific overrides.
 */
const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  schemaJson,
  type = "website",
  article,
}: SEOHeadProps) => {
  // Ensure canonical is absolute URL
  const canonicalUrl = canonical.startsWith("http") ? canonical : `${BASE_URL}${canonical}`;
  
  // Ensure ogImage is absolute URL
  const absoluteOgImage = ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`;
  
  // Format title with site name if not already included
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  // Handle multiple schema objects
  const renderSchemaJson = () => {
    if (!schemaJson) return null;
    
    if (Array.isArray(schemaJson)) {
      return schemaJson.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ));
    }
    
    return (
      <script type="application/ld+json">
        {JSON.stringify(schemaJson)}
      </script>
    );
  };

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang - bilingual signal for Google */}
      <link rel="alternate" hrefLang="fr-CA" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-CA" href={canonicalUrl.replace("agence8020.com", "agence8020.com/en")} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph - Complete Set */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="fr_CA" />
      
      {/* Article-specific OG tags */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {article?.section && (
        <meta property="article:section" content={article.section} />
      )}
      
      {/* Twitter Card - Complete Set */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteOgImage} />
      
      {/* Structured Data */}
      {renderSchemaJson()}
    </Helmet>
  );
};

export default SEOHead;
